import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Circle, MinusCircle, Send, XCircle } from "lucide-react";
import { api, errMsg, fmtDate, fmtMoney } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_CLS = {
  pending_manager: "bg-amber-50 text-amber-800", pending_direction: "bg-blue-50 text-[#1E3A8A]",
  approved: "bg-emerald-50 text-emerald-700", rejected: "bg-red-50 text-red-700",
};

export const StatusBadge = ({ a }) => (
  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${STATUS_CLS[a.status]}`} data-testid={`approval-status-${a.id}`}>{a.status_label}</span>
);

export function Timeline({ a }) {
  const steps = [
    { key: "soumission", label: `Soumis par ${a.submitted_by_name}` },
    { key: "responsable", label: a.mode === "specific" ? `Responsable : ${a.reviewer_name}` : "Un responsable" },
    { key: "direction", label: "Direction (administrateur)" },
  ];
  return (
    <ol className="space-y-3" data-testid={`approval-timeline-${a.id}`}>
      {steps.map((s) => {
        const h = [...a.history].reverse().find((x) => x.step === s.key);
        const Icon = !h ? Circle : h.action === "rejected" ? XCircle : h.action === "skipped" ? MinusCircle : CheckCircle2;
        const color = !h ? "text-slate-300" : h.action === "rejected" ? "text-red-600" : h.action === "skipped" ? "text-slate-400" : "text-emerald-600";
        return (
          <li key={s.key} className="flex gap-3">
            <Icon className={`h-5 w-5 shrink-0 ${color}`} />
            <div className="text-sm">
              <div className="font-medium">{s.label}{h?.action === "skipped" && <span className="text-slate-500 font-normal"> — non requise</span>}</div>
              {h && <div className="text-xs text-slate-500">{h.by_name} · {fmtDate(h.at, true)}{h.comment && <span className="block text-slate-700 italic mt-0.5">« {h.comment} »</span>}</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export function DecisionBox({ a, onDone }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const decide = async (decision) => {
    setBusy(true);
    try { await api.post(`/approvals/${a.id}/decision`, { decision, comment }); toast.success(decision === "approve" ? "Validé" : "Refusé"); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Commentaire (obligatoire en cas de refus)" data-testid={`approval-comment-${a.id}`} />
      <div className="flex gap-2">
        <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => decide("approve")} data-testid={`approval-approve-${a.id}`}>Valider</Button>
        <Button variant="outline" className="text-red-600" disabled={busy} onClick={() => decide("reject")} data-testid={`approval-reject-${a.id}`}>Refuser</Button>
      </div>
    </div>
  );
}

export function ApprovalTab({ file, onChanged }) {
  const { me } = useMe();
  const [items, setItems] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [target, setTarget] = useState("any");
  const [comment, setComment] = useState("");
  const [threshold, setThreshold] = useState(null);
  const load = () => api.get(`/files/${file.id}/approvals`).then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/reviewers").then((r) => setReviewers(r.data)).catch(() => {});
    api.get("/approvals/rules").then((r) => setThreshold(r.data.approval_threshold)).catch(() => {});
  }, [file.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const active = items.find((a) => ["pending_manager", "pending_direction"].includes(a.status));
  const submit = async () => {
    try {
      await api.post("/approvals", { file_id: file.id, mode: target === "any" ? "any" : "specific", reviewer_id: target === "any" ? null : target, comment });
      toast.success("Demande de validation envoyée"); setComment(""); load(); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4">
      {!active && me.role !== "externe" && (
        <div className="bg-white border rounded-lg p-4 space-y-3" data-testid="approval-submit-form">
          <h4 className="font-semibold text-sm flex items-center gap-2"><Send className="h-4 w-4" />Soumettre à validation</h4>
          <p className="text-xs text-slate-500" data-testid="approval-rule-hint">
            {threshold != null
              ? <>Circuit : responsable, puis direction si le montant TTC atteint {fmtMoney(threshold)} (ou s'il n'est pas connu).</>
              : "Circuit : responsable, puis direction (administrateur)."}
          </p>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger data-testid="approval-reviewer-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">N'importe quel responsable</SelectItem>
              {reviewers.filter((r) => r.user_id !== me.user_id).map((r) => <SelectItem key={r.user_id} value={r.user_id}>{r.name} ({r.role === "admin" ? "administrateur" : "responsable"})</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Message pour le valideur (facultatif)" data-testid="approval-submit-comment" />
          <Button onClick={submit} data-testid="approval-submit-btn">Envoyer la demande</Button>
        </div>
      )}
      {items.length === 0 && <p className="text-sm text-slate-500">Aucune demande de validation pour ce document.</p>}
      {items.map((a) => (
        <div key={a.id} className="bg-white border rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between"><span className="text-xs text-slate-500">Demande du {fmtDate(a.created_at, true)}</span><StatusBadge a={a} /></div>
          <Timeline a={a} />
          {a.can_act && <DecisionBox a={a} onDone={() => { load(); onChanged(); }} />}
        </div>
      ))}
    </div>
  );
}
